'use client';

import { Buildings, EmptyWindow } from 'phosphor-icons';
import { useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { c } from '../../../utils/tailwind-merge';
import { Avatar } from '../../atoms/Avatar';
import { ButtonV2 } from '../../atoms/Button/Button.v2';
import { InputV2 } from '../../atoms/Input.v2';
import { FormModal } from '../../molecules/FormModal';
import { ShareListModalProps } from './ShareListModal.types';

export const ShareListModal = ({
  type,
  typeName,
  name,
  count,
  link,
  microcopy,
  className,
  trigger: triggerProp,
}: ShareListModalProps) => {
  const [copied, setCopied] = useState(false);

  const trigger = triggerProp || (
    <ButtonV2
      label="Share"
      variant="tertiary-blue-monochrome"
      size="small"
      shape="square"
      iconLeft="ShareNetwork"
    />
  );

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = link;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
  const twitterUrl = `https://x.com/intent/post?text=${encodeURIComponent(
    interpolateStringWithData(microcopy.twitterShareMessage, { name, link })
  )}&source=webclient`;
  const emailUrl = `mailto:?subject=${encodeURIComponent(
    `${name} via Unity Asset Store`
  )}&body=${encodeURIComponent(
    interpolateStringWithData(microcopy.emailShareMessage, {
      name,
      link,
      publisher: typeName,
    })
  )}`;

  const renderContent = () => (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3 rounded-lg px-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-grey-800">
          <EmptyWindow size={50} />
        </div>
        <div className="flex flex-col justify-center gap-0.5 overflow-hidden">
          <div className="flex items-center gap-1.5">
            {type === 'organization' ? (
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-grey-800">
                <Buildings
                  size={12}
                  weight="regular"
                  className="fill-grey-400"
                />
              </div>
            ) : (
              <Avatar
                userName={typeName}
                size={20}
                className="shrink-0 bg-grey-800"
                textClassName="text-[10px] text-grey-400"
              />
            )}
            <span className="truncate text-xs text-grey-400">{typeName}</span>
          </div>
          <p className="truncate text-base text-grey-100">{name}</p>
          <span className="-mt-0.5 text-xs text-grey-400">
            {interpolateStringWithData(microcopy.assets, {
              count,
            })}
          </span>
        </div>
      </div>

      <div className="border-t border-grey-800 p-4">
        <div className="flex gap-3">
          <ButtonV2
            variant="primary"
            size="medium"
            shape="square"
            iconLeft="FacebookLogo"
            iconClassName="fill-white"
            iconWeight="fill"
            iconSize={20}
            onClick={() =>
              window.open(facebookUrl, '_blank', 'noopener,noreferrer')
            }
            aria-label={microcopy.facebookAria}
            className="flex-1 !rounded-lg"
          />
          <ButtonV2
            variant="primary"
            size="medium"
            shape="square"
            iconLeft="TwitterLogo"
            iconClassName="fill-white"
            iconWeight="fill"
            iconSize={20}
            onClick={() =>
              window.open(twitterUrl, '_blank', 'noopener,noreferrer')
            }
            aria-label={microcopy.twitterAria}
            className="flex-1 !rounded-lg"
          />
          <ButtonV2
            variant="primary"
            size="medium"
            shape="square"
            iconLeft="Envelope"
            iconClassName="fill-white"
            iconWeight="fill"
            iconSize={20}
            onClick={() => window.open(emailUrl, '_self')}
            aria-label={microcopy.emailAria}
            className="flex-1 !rounded-lg"
          />
        </div>

        <div className="relative mt-3">
          <InputV2
            readOnly
            value={link}
            dark
            className="w-full truncate px-3 py-2 pr-20 text-sm text-grey-100"
          />
          <ButtonV2
            variant="tertiary-blue-monochrome"
            size="xsmall"
            shape="square"
            label={copied ? microcopy.copied : microcopy.copyUrl}
            onClick={handleCopyUrl}
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-grey-800 hover:bg-grey-800 active:bg-grey-800"
          />
        </div>
      </div>
    </div>
  );

  return (
    <FormModal
      dark
      size="xsmall"
      title={microcopy.title}
      subtitle={microcopy.subTitle}
      close={microcopy.close}
      trigger={trigger}
      content={renderContent()}
      className={'h-auto px-0'}
      headerClassName={c('px-4', className)}
    />
  );
};
